<template>
  <div>
    <div class="setup-group-head"><span>Quality settings</span><span class="line"></span></div>

    <!-- Visual & helpful content -->
    <setup-collapse title="Visual & helpful content" :summary="visualSummary" done>
      <span class="field-label">How visual should this page be?</span>
      <div class="help-text" style="margin-bottom: 10px;">Default reflects your page type. Article → Magazine-style. Blog post → Balanced. Product/Category → SEO-heavy.</div>
      <div class="chip-row" style="margin-bottom: 16px;">
        <button
          v-for="d in VISUAL_DENSITIES"
          :key="d.value"
          class="chip"
          :class="{ selected: model.visual_density === d.value, recommended: d.value === 'magazine_style' }"
          @click="applyPreset(d.value)"
        >{{ d.label }}</button>
      </div>

      <div class="rc-rec-banner">
        <span class="ico">i</span>
        Set the amount for each component you'd like POP AI Writer to generate. Rich content recommendations are based on your POP Content prompts report.
      </div>

      <table class="rc-table">
        <thead>
          <tr>
            <th style="width: 40px;">On</th>
            <th>Component</th>
            <th>Recommended</th>
            <th class="center" style="width: 80px;">Quantity</th>
            <th class="right" style="width: 90px;">Cost</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="comp in VISUAL_COMPONENTS" :key="comp.key" :class="{ 'over-rec': showWarn(comp) }">
            <td><button class="toggle" :class="{ on: cmp(comp.key).enabled }" @click="toggleComponent(comp.key)"></button></td>
            <td class="comp-name">
              {{ comp.label }}
              <div v-if="comp.warnable" class="rc-warn"><span class="warn-ico">⚠</span><span class="warn-text">{{ warnText(comp) }}</span></div>
              <div v-if="showError(comp)" class="rc-error"><span class="err-ico">!</span><span class="err-text">{{ errorText(comp) }}</span></div>
            </td>
            <td class="rec-range">{{ comp.recommended }}</td>
            <td class="center">
              <input class="qty-input" :class="{ over: showWarn(comp), 'over-error': showError(comp) }" :value="cmp(comp.key).quantity" @input="setQuantity(comp.key, $event)" />
            </td>
            <td style="text-align: right;"><span class="rc-cost" :class="{ free: rowCredits(comp) === 0 }">{{ rowCostLabel(comp) }}</span></td>
          </tr>
        </tbody>
      </table>
      <div v-if="anthropicOwnKey && cmp('images').enabled" class="rc-anthropic-img-note">
        <span class="ico">i</span>
        <span>Images still cost 1 credit each — an OpenAI image model generates them, even on your Claude key.</span>
      </div>

      <!-- Internal links setup (revealed when Internal links is ON) -->
      <div v-if="cmp('internal_links').enabled" class="internal-links-setup">
        <div class="ils-head">
          <span class="ils-badge">Required for internal links</span>
          <span class="ils-sub">Add the pages POP should link to, so it can place links naturally in your draft.</span>
        </div>
        <div class="ils-field">
          <label>Top-level page link <span class="ils-hint">(main target page)</span></label>
          <input class="text-input" :class="{ invalid: topLevelInvalid }" type="url" placeholder="https://www.example.com/product" v-model="model.internal_links.top_level_url" @input="onInternalLinks" />
          <span v-if="topLevelInvalid" class="ils-error">{{ topLevelError }}</span>
        </div>
        <p class="ils-note">Add up to two closely-related pages that support the main page topic.</p>
        <div class="ils-field">
          <label>Supporting page link 1</label>
          <input class="text-input" :class="{ invalid: supportingInvalid[0] }" type="url" placeholder="https://www.example.com/product-details" v-model="model.internal_links.supporting_urls[0]" @input="onInternalLinks" />
          <span v-if="supportingInvalid[0]" class="ils-error">Enter a valid URL (https://…).</span>
        </div>
        <div class="ils-field">
          <label>Supporting page link 2</label>
          <input class="text-input" :class="{ invalid: supportingInvalid[1] }" type="url" placeholder="https://www.example.com/product-reviews" v-model="model.internal_links.supporting_urls[1]" @input="onInternalLinks" />
          <span v-if="supportingInvalid[1]" class="ils-error">Enter a valid URL (https://…).</span>
        </div>
      </div>

      <!-- Image style board -->
      <div class="img-style-board">
        <div class="img-style-board-head">Image style <span class="cost-chip"><span class="spark">⚡</span> 1 credit per image</span></div>
        <div class="img-style-grid">
          <button
            v-for="s in IMAGE_STYLES"
            :key="s.value"
            class="img-style-card"
            :class="{ selected: model.image_style === s.value }"
            @click="selectImageStyle(s.value)"
          >
            <div class="preview" :style="{ background: GRADIENTS[s.value] }">{{ s.icon }}</div>
            <div class="name">{{ s.label }}</div>
            <div class="desc">{{ s.description }}</div>
          </button>
        </div>
      </div>
    </setup-collapse>

    <!-- SEO intensity -->
    <setup-collapse title="SEO intensity" :summary="seoSummary" :note="seoNote" done>
      <span class="field-label">How hard should POP optimize for ranking?</span>
      <div class="help-text" style="margin-bottom: 12px;">Higher intensity → more terms, more rich content, more SEO structure. Lower intensity → more natural reading.</div>
      <div class="intensity-grid">
        <button
          v-for="s in SEO_INTENSITIES"
          :key="s.value"
          class="intensity-card"
          :class="{ selected: model.seo_intensity === s.value }"
          @click="selectSeo(s.value)"
        >
          <div class="badge-rec">{{ s.badge }}</div>
          <div class="name">{{ s.label }}</div>
          <div class="desc">{{ s.description }}</div>
        </button>
      </div>

      <details class="adv-sub">
        <summary>Advanced - Writing priority</summary>
        <div style="padding: 10px 0 0;">
          <div class="help-text" style="margin-bottom: 10px;">Fine-tunes how POP balances ranking against readability. Usually derived from SEO intensity above - only adjust for explicit control.</div>
          <div class="radio-list">
            <label
              v-for="p in WRITING_PRIORITIES"
              :key="p.value"
              class="radio-row"
              :class="{ selected: model.writing_priority === p.value }"
              @click="setPriority(p.value)"
            >
              <span class="radio-dot"></span>
              <span><strong>{{ labelParts(p.label).head }}</strong>{{ labelParts(p.label).tail }}</span>
              <span v-if="p.badge" class="recommend-badge">{{ p.badge }}</span>
            </label>
          </div>
        </div>
      </details>
    </setup-collapse>

    <!-- Writing style -->
    <setup-collapse title="Writing style" :summary="styleSummary" done>
      <span class="field-label">Voice</span>
      <div class="help-text" style="margin-bottom: 10px;">Pick one or more. POP will blend them in the writing.</div>
      <div class="chip-row">
        <button
          v-for="s in WRITING_STYLES"
          :key="s.value"
          class="chip"
          :class="{ selected: model.content_style.includes(s.value), recommended: RECOMMENDED_STYLES.includes(s.value) }"
          @click="toggleStyle(s.value)"
        >{{ s.label }}</button>
        <button v-for="c in customStyles" :key="'c-' + c" class="chip selected" @click="toggleStyle(c)">{{ c }} ×</button>
        <button class="chip add" @click="addCustomStyle">+ Custom</button>
      </div>
      <div class="voice-preview">
        <span class="label">Example voice</span>
        "I'd choose Venice or Verona if I were renting a car, but Bolzano is usually easier if you want a train-first route."
      </div>
    </setup-collapse>

    <!-- Custom voice modal -->
    <div v-if="customModal.show" class="aiw2-overlay" @click.self="closeCustomModal">
      <div class="aiw2-modal">
        <h4>Add a custom voice</h4>
        <p>Describe the writing voice POP should blend in.</p>
        <input ref="customInput" class="aiw2-input" type="text" v-model="customModal.value" placeholder='e.g. "Witty and bold"' @keyup.enter="confirmCustomStyle" />
        <div class="modal-actions">
          <button class="aiw2-btn" @click="closeCustomModal">Cancel</button>
          <button class="aiw2-btn aiw2-btn--primary" :disabled="!customModal.value.trim()" @click="confirmCustomStyle">Add</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SetupCollapse from "./SetupCollapse.vue";
import {
  VISUAL_DENSITIES, VISUAL_COMPONENTS, DENSITY_PRESETS, IMAGE_STYLES,
  SEO_INTENSITIES, WRITING_PRIORITIES, WRITING_STYLES, recommendedMax, recommendedHardMax,
} from "../../constants";

const GRADIENTS = {
  editorial: "linear-gradient(135deg,#6B5B95,#B8A9C9)",
  cinematic: "linear-gradient(135deg,#2C3E50,#FD746C)",
  watercolor: "linear-gradient(135deg,#56CCF2,#2F80ED)",
  flat_vector: "linear-gradient(135deg,#11998e,#38ef7d)",
  hand_drawn: "linear-gradient(135deg,#D38312,#A83279)",
  vintage_poster: "linear-gradient(135deg,#8E2DE2,#4A00E0)",
  minimal: "linear-gradient(135deg,#bdc3c7,#2c3e50)",
  three_d_render: "linear-gradient(135deg,#373B44,#4286f4)",
};
const RECOMMENDED_STYLES = ["premium_editorial", "local_expert"];

export default {
  name: "AiwV2QualitySettingsSection",
  components: { SetupCollapse },
  props: { model: { type: Object, required: true } },
  data() {
    return {
      VISUAL_DENSITIES, VISUAL_COMPONENTS, IMAGE_STYLES, SEO_INTENSITIES,
      WRITING_PRIORITIES, WRITING_STYLES, GRADIENTS, RECOMMENDED_STYLES,
      customModal: { show: false, value: "" },
      // Components the user set by hand. A density preset must not silently undo
      // them -- that's how an enabled component (or internal links, whose URLs
      // stay on screen) quietly ended up back at 0 and generated nothing.
      touchedComponents: {},
    };
  },
  computed: {
    // Claude own-key runs have no image API — images run on POP's image model
    // and keep their per-image credit cost.
    anthropicOwnKey() {
      return !!(this.model.advanced && this.model.advanced.api_mode === "anthropic_key");
    },
    visualSummary() {
      const d = VISUAL_DENSITIES.find((x) => x.value === this.model.visual_density);
      const on = VISUAL_COMPONENTS
        .filter((c) => this.cmp(c.key).enabled && this.cmp(c.key).quantity > 0)
        .map((c) => `${this.cmp(c.key).quantity} ${c.label.toLowerCase()}`);
      return [d ? d.label : "", on.join(", ")].filter(Boolean).join(" · ");
    },
    seoSummary() {
      const s = SEO_INTENSITIES.find((x) => x.value === this.model.seo_intensity);
      return s ? s.label : "";
    },
    seoNote() {
      const s = SEO_INTENSITIES.find((x) => x.value === this.model.seo_intensity);
      return s && s.tagline ? "- " + s.tagline : "";
    },
    styleSummary() {
      return (this.model.content_style || [])
        .map((v) => (WRITING_STYLES.find((s) => s.value === v) || {}).label || v)
        .join(" · ");
    },
    customStyles() {
      // Selected voices that aren't one of the predefined chips (user-added).
      const known = new Set(WRITING_STYLES.map((s) => s.value));
      return (this.model.content_style || []).filter((v) => !known.has(v));
    },
    ilEnabled() {
      return !!this.cmp("internal_links").enabled;
    },
    topLevelInvalid() {
      if (!this.ilEnabled) return false;
      const u = (this.model.internal_links.top_level_url || "").trim();
      return !u || !this.ilUrlOk(u);
    },
    topLevelError() {
      const u = (this.model.internal_links.top_level_url || "").trim();
      return !u ? "Top-level page URL is required." : "Enter a valid URL (https://…).";
    },
    supportingInvalid() {
      if (!this.ilEnabled) return [false, false];
      return (this.model.internal_links.supporting_urls || []).map((u) => {
        const t = (u || "").trim();
        return !!t && !this.ilUrlOk(t);
      });
    },
  },
  methods: {
    emit() { this.$emit("change"); },
    cmp(key) {
      return this.model.visual_components[key] || { enabled: false, quantity: 0 };
    },
    rowCredits(comp) {
      const c = this.cmp(comp.key);
      return c.enabled ? (comp.cost || 0) * (c.quantity || 0) : 0;
    },
    rowCostLabel(comp) {
      const n = this.rowCredits(comp);
      return `${n} credit${n === 1 ? "" : "s"}`;
    },
    overRecommended(comp) {
      // Flags an enabled component whose quantity exceeds its recommended max
      // (drives the amber qty-input state).
      const max = recommendedMax(comp.recommended);
      const c = this.cmp(comp.key);
      return max != null && c.enabled && c.quantity > max;
    },
    showWarn(comp) {
      // Only Images/Lists/Tables get the "up to double" soft warning + note.
      return !!comp.warnable && this.overRecommended(comp);
    },
    warnText(comp) {
      const recMax = recommendedMax(comp.recommended);
      const hardMax = recommendedHardMax(comp.recommended);
      return `We don’t recommend more than ${recMax} - extra ${comp.label.toLowerCase()} can dilute your content and lower readability. ${hardMax} is the most you can add.`;
    },
    showError(comp) {
      // Non-warnable components can't exceed their recommended max — going over
      // blocks "Generate", so flag them distinctly (red) from the amber warning.
      return !comp.warnable && this.overRecommended(comp);
    },
    errorText(comp) {
      return `Reduce to ${recommendedMax(comp.recommended)} or fewer to continue.`;
    },
    applyPreset(density) {
      this.model.visual_density = density;
      const preset = DENSITY_PRESETS[density] || {};
      VISUAL_COMPONENTS.forEach((c) => {
        if (this.touchedComponents[c.key]) return;   // explicit choice beats the preset
        const qty = preset[c.key] || 0;
        this.model.visual_components[c.key].quantity = qty;
        this.model.visual_components[c.key].enabled = qty > 0;
      });
      this.syncInternalLinks();
      this.emit();
    },
    toggleComponent(key) {
      const c = this.model.visual_components[key];
      c.enabled = !c.enabled;
      if (c.enabled && !c.quantity) c.quantity = 1;
      this.$set(this.touchedComponents, key, true);
      this.syncInternalLinks();
      this.emit();
    },
    setQuantity(key, event) {
      let qty = Math.max(0, parseInt(event.target.value, 10) || 0);
      // Images/Lists/Tables may go up to double the recommended max; clamp there.
      const comp = VISUAL_COMPONENTS.find((c) => c.key === key);
      if (comp && comp.warnable) {
        const hardMax = recommendedHardMax(comp.recommended);
        if (hardMax != null) qty = Math.min(qty, hardMax);
      }
      const c = this.model.visual_components[key];
      c.quantity = qty;
      c.enabled = qty > 0;
      this.$set(this.touchedComponents, key, true);
      // Vue 2 won't overwrite a focused input's value via :value, so write the
      // clamped/normalized value into the box directly (matches the design's
      // `input.value = hardMax`) — otherwise the user could keep an over-limit
      // number visible even though the model is clamped.
      if (event.target.value !== String(qty)) event.target.value = String(qty);
      this.syncInternalLinks();
      this.emit();
    },
    syncInternalLinks() {
      // Keep model.internal_links.enabled in lockstep with the visual-component
      // toggle — backend validation/injection key off model.internal_links.enabled.
      this.model.internal_links.enabled = !!this.cmp("internal_links").enabled;
    },
    ilUrlOk(u) {
      try {
        const x = new URL((u || "").trim());
        return x.protocol === "http:" || x.protocol === "https:";
      } catch (e) {
        return false;
      }
    },
    selectImageStyle(value) { this.model.image_style = value; this.emit(); },
    selectSeo(value) { this.model.seo_intensity = value; this.emit(); },
    setPriority(value) { this.model.writing_priority = value; this.emit(); },
    // Split a "Head - description" option label so the head renders bold (design).
    labelParts(label) {
      const s = String(label || "");
      const i = s.indexOf(" - ");
      if (i === -1) return { head: s, tail: "" };
      return { head: s.slice(0, i).trim(), tail: " " + s.slice(i).trim() };
    },
    toggleStyle(value) {
      const arr = this.model.content_style;
      const i = arr.indexOf(value);
      if (i === -1) arr.push(value);
      else arr.splice(i, 1);
      this.emit();
    },
    addCustomStyle() {
      this.customModal = { show: true, value: "" };
      this.$nextTick(() => { if (this.$refs.customInput) this.$refs.customInput.focus(); });
    },
    confirmCustomStyle() {
      const v = (this.customModal.value || "").trim();
      if (v && !this.model.content_style.includes(v)) {
        this.model.content_style.push(v);
        this.emit();
      }
      this.closeCustomModal();
    },
    closeCustomModal() {
      this.customModal = { show: false, value: "" };
    },
    onInternalLinks() { this.emit(); },
  },
};
</script>

<style scoped>
/* Image-style board + internal-links setup are unique to this section. */
.img-style-board { margin-top: 18px; }
.img-style-board-head { font-weight: 700; font-size: 13.5px; display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.cost-chip { font-size: 11px; font-weight: 600; color: var(--pop-orange-deep, #b23d00); background: var(--pop-orange-soft, #ffeddd); border: 1px solid var(--pop-orange-tint, #ffd9c2); border-radius: 999px; padding: 2px 9px; display: inline-flex; align-items: center; gap: 4px; }
.cost-chip .spark { color: var(--pop-orange, #ff5700); }
.img-style-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
.img-style-card { text-align: left; border: 1.5px solid var(--border, #e4e4e4); border-radius: 8px; padding: 8px; background: #fff; cursor: pointer; font-family: inherit; }
.img-style-card.selected { border-color: var(--pop-orange, #ff5700); background: var(--pop-orange-soft, #ffeddd); }
.img-style-card .preview { height: 54px; border-radius: 6px; display: flex; align-items: center; justify-content: center; font-size: 20px; margin-bottom: 8px; }
.img-style-card .name { font-weight: 700; font-size: 12px; color: var(--text, #052e33); }
.img-style-card .desc { font-size: 11px; color: var(--text-3, #6b7b7e); line-height: 1.35; margin-top: 2px; }
@media (max-width: 720px) { .img-style-grid { grid-template-columns: repeat(2, 1fr); } }

/* Quantity above the recommended max — mirrors the .invalid field treatment;
   the bottom-bar "Continue" stays disabled until it's brought back in range. */
.rc-table .qty-input.over { border-color: #E8B23A; background: var(--pop-amber-soft, #faf3d0); color: #92400E; font-weight: 700; }
.rc-table .rc-warn { display: none; margin-top: 5px; font-size: 11px; font-weight: 600; line-height: 1.4; color: #92400E; align-items: flex-start; gap: 5px; max-width: 340px; }
/* One-line muted hint on Claude own-key runs (images bill normally). */
.rc-anthropic-img-note { display: flex; align-items: flex-start; gap: 6px; margin-top: 8px; font-size: 12px; color: var(--text-2, #47606a); }
.rc-anthropic-img-note .ico { flex: none; width: 14px; height: 14px; border-radius: 50%; background: #014751; color: #fff; display: inline-flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; margin-top: 2px; }
.rc-table .over-rec .rc-warn { display: flex; }
.rc-table .rc-warn .warn-ico { flex-shrink: 0; color: #E8B23A; font-size: 12px; line-height: 1.3; }
/* Non-warnable component over its recommended max — a hard error that blocks
   "Generate" (red, distinct from the amber "allowed up to 2×" warning above). */
.rc-table .qty-input.over-error { border-color: var(--pop-red, #ff3939); background: #fff6f6; color: var(--pop-red, #ff3939); font-weight: 700; }
.rc-table .rc-error { margin-top: 5px; font-size: 11px; font-weight: 600; line-height: 1.4; color: var(--pop-red, #ff3939); display: flex; align-items: center; gap: 5px; max-width: 340px; }
.rc-table .rc-error .err-ico { flex-shrink: 0; box-sizing: border-box; width: 14px; height: 14px; border-radius: 50%; border: 1px solid var(--pop-red, #ff3939); background: transparent; color: var(--pop-red, #ff3939); display: inline-flex; align-items: center; justify-content: center; font-size: 9px; font-weight: 700; line-height: 1; }

.internal-links-setup { margin-top: 14px; padding: 18px 20px; border: 1px solid var(--border, #e4e4e4); border-radius: var(--r-lg, 10px); background: var(--surface-2, #f7f7f7); }
.ils-head { display: flex; flex-direction: column; gap: 4px; margin-bottom: 14px; }
.ils-badge { align-self: flex-start; font-size: 10px; font-weight: 800; letter-spacing: .03em; text-transform: uppercase; color: #d12626; background: #fbe8e8; padding: 3px 9px; border-radius: 5px; }
.ils-sub, .ils-note { font-size: 12.5px; color: var(--text-3, #6b7b7e); line-height: 1.5; }
.ils-note { margin: 4px 0 12px; }
.ils-field { display: flex; flex-direction: column; gap: 5px; margin-bottom: 12px; }
.ils-field label { font-size: 12.5px; font-weight: 700; color: var(--text-2, #014751); }
.ils-hint { color: var(--text-3, #6b7b7e); font-weight: 400; }
.ils-field .text-input.invalid { border-color: var(--pop-red, #ff3939); background: #fff6f6; }
.ils-error { display: block; font-size: 11.5px; color: var(--pop-red, #ff3939); margin-top: 4px; }
/* Custom voice modal (buttons/input use the global .aiw2-btn / .aiw2-input) */
.aiw2-overlay { position: fixed; inset: 0; background: rgba(5,20,25,.35); display: flex; align-items: center; justify-content: center; z-index: 60; }
.aiw2-modal { background: #fff; border-radius: var(--r-lg, 10px); padding: 22px; width: 420px; max-width: 92vw; box-shadow: var(--shadow-lg, 0 10px 36px rgba(15,20,25,.08)); font-family: "Mulish", system-ui, sans-serif; }
.aiw2-modal h4 { font-size: 1rem; font-weight: 800; margin: 0 0 8px; color: var(--text, #052e33); }
.aiw2-modal p { font-size: 0.85rem; color: var(--text-2, #014751); margin: 0 0 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 10px; margin-top: 14px; }
</style>
